import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/authStore';
import { toast } from '@/store/toastStore';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

/** Instance axios principale (intercepteurs JWT). */
export const api = axios.create({
  baseURL: `${API_BASE}/v1`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

/** Instance « nue » sans intercepteur, pour le refresh (évite les boucles). */
const bareApi = axios.create({ baseURL: `${API_BASE}/v1` });

/**
 * Convertit récursivement les chaînes vides en `null`. Un champ optionnel laissé
 * vide dans un formulaire envoie `""`, ce que la validation backend rejette pour
 * les types non-textuels (date, nombre, email…). On normalise donc côté réseau :
 * vide = non renseigné = null. Couvre TOUS les formulaires sans les modifier.
 */
function blankToNull(value: unknown): unknown {
  if (value === '') return null;
  if (Array.isArray(value)) return value.map(blankToNull);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = blankToNull(v);
    return out;
  }
  return value;
}

/** Vrai uniquement pour un objet/tableau JSON « simple » (≠ URLSearchParams,
 *  FormData, Blob… qui ne doivent surtout pas être réécrits — ex. login OAuth2). */
function isPlainJsonBody(value: unknown): boolean {
  if (Array.isArray(value)) return true;
  if (!value || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

// Attache le jeton d'accès + normalise le corps JSON (chaînes vides -> null).
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (isPlainJsonBody(config.data)) {
    config.data = blankToNull(config.data);
  }
  return config;
});

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) throw new Error('Aucun refresh token');
  const { data } = await bareApi.post('/auth/refresh', { refresh_token: refreshToken });
  useAuthStore.getState().setTokens(data.access_token, data.refresh_token);
  return data.access_token as string;
}

// Sur 401 : tente un refresh unique puis rejoue la requête ; sinon déconnecte.
api.interceptors.response.use(
  (response) => {
    // Garde-fous : avertissement NON bloquant renvoyé par le serveur (banque négative, date future…).
    const avert = response.headers?.['x-avertissement'];
    if (avert) {
      try { toast.warning(decodeURIComponent(avert)); } catch { toast.warning(String(avert)); }
    }
    return response;
  },
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const isAuthCall = original?.url?.includes('/auth/');

    if (status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        refreshPromise ??= refreshAccessToken();
        const newToken = await refreshPromise;
        refreshPromise = null;
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (refreshError) {
        refreshPromise = null;
        useAuthStore.getState().clearSession();
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

/** Extrait un message d'erreur lisible d'une erreur axios. */
export function getApiErrorMessage(error: unknown, fallback = 'Une erreur est survenue.'): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg);
    return error.message || fallback;
  }
  return fallback;
}
